import { memo, useCallback, useEffect, useMemo, useState, useContext, type FunctionComponent } from 'react';
import size from 'lodash/size';
import take from 'lodash/take';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import includes from 'lodash/includes';
import { useLazyQuery } from '@apollo/client';
// Material UI imports
import { type SelectChangeEvent } from '@mui/material/Select';
// Skillmore UI Components
import { pathBuilder } from '@empathco/ui-components/src/helpers/graphql';
import { POLLING_FETCH_POLICY } from '@empathco/ui-components/src/hooks/usePolling';
import useQueryObject from '@empathco/ui-components/src/hooks/useQueryObject';
import OnOffSwitch from '@empathco/ui-components/src/elements/OnOffSwitch';
import ScopeSelector from '@empathco/ui-components/src/elements/ScopeSelector';
// local imports
import { DA_CLOSE_MATCH_JOBS_QUERY } from '../graphql/DACloseMatchJobs';
import { DA_AVERAGE_SKILLS_QUERY } from '../graphql/DAAverageSkills';
import { DA_EMPLOYEE_VELOCITY_QUERY } from '../graphql/DAEmployeeVelocity';
import { DA_CURRENT_ROLE_MATCH_QUERY } from '../graphql/DACurrentRoleMatch';
import { DA_JOB_MOVEMENT_SKILLS_QUERY } from '../graphql/DAJobMovementSkills';
import { DA_USAGE_BY_ORG_QUERY } from '../graphql/DAOrgUsage';
import {
  SkillType,
  DACloseMatchJobsDocument, DACloseMatchJobsQuery,
  DAAverageSkillsDocument, DAAverageSkillsQuery,
  DAEmployeeVelocityDocument, DAEmployeeVelocityQuery,
  DACurrentRoleMatchDocument, DACurrentRoleMatchQuery,
  DAJobMovementSkillsDocument, DAJobMovementSkillsQuery,
  DAOrgUsageDocument, DAOrgUsageQuery
} from '../graphql/types';
import { CONST_MATCH_RATE, toValidConst } from '../constants/constValues';
import { JOB_MOVEMENT_SCOPE_UP, JOB_MOVEMENT_SKILL_SCOPES } from '../constants/scopes';
import {
  DA_AVERAGE_SKILLS, DA_CLOSE_MATCH_JOBS, DA_CURRENT_ROLE_MATCH, DA_EMPLOYEE_VELOCITY, DA_JOB_MOVEMENT_SKILLS,
  DA_USAGE_BY_ORG
} from '../constants/dashboardAnalytics';
import { MAX_DURATION, MAX_MANAGER_DASHBOARD_ITEMS } from '../config/params';
import useCustomerSettings from '../config/customer';
import {
  API_DA_AVERAGE_SKILLS_EXPORT, API_DA_CLOSE_MATCH_JOBS_EXPORT, API_DA_CURRENT_ROLE_MATCH_EXPORT,
  API_DA_EMPLOYEE_VELOCITY_EXPORT, API_DA_JOB_MOVEMENT_SKILLS_EXPORT, API_DA_USAGE_BY_ORG_EXPORT
} from '../config/api';
import { PATH_SKILL } from '../config/paths';
import { omitParamsBySkillType } from '../helpers/analytics';
import { getScopeFetchMessage, isScopeRefused } from '../helpers/graphql';
import { FilterValues } from '../hooks/useFilters';
import useChartPolling from '../hooks/useChartStatus';
import { GlobalContext } from '../context/global';
import ConstSelector from '../elements/ConstSelector';
import TopChart from '../widgets/TopChart';
// SCSS imports
import { matchRateSelector } from './DashboardAnalyticsEmployees.module.scss';

type DashboardAnalyticsEmployeesProps = {
  readonly chartId: number;
  readonly skillType: SkillType;
  readonly filters?: FilterValues;
  readonly years: number;
  readonly pending?: boolean | null;
  readonly disabled?: boolean | null;
  readonly onEmployeeCountChange?: (value: number | null | undefined) => void;
  readonly onScopeRefused?: () => void;
  readonly pinned?: number[];
  readonly onPin: (value: number) => void;
  readonly pinning?: boolean;
}

const DashboardAnalyticsEmployees: FunctionComponent<DashboardAnalyticsEmployeesProps> = ({
  chartId,
  skillType,
  filters,
  years,
  pending: parentPending = false,
  disabled = false,
  onEmployeeCountChange,
  onScopeRefused,
  pinned,
  onPin,
  pinning = false
  // eslint-disable-next-line complexity, max-lines-per-function, max-statements
}) => {
  const { DA_TABS, HAS_INDEMAND_SKILLS } = useCustomerSettings();
  const { fontsLoaded, token } = useContext(GlobalContext);
  const pending = parentPending || !fontsLoaded;

  // lazy load Job Movement Skills data
  const {
    query: getMovementSkills, pending: pendingMovementSkills, failed: failedMovementSkills, results: movementSkills,
    error: errorMovementSkills
  } = useQueryObject({
    data: undefined as unknown as DAJobMovementSkillsQuery['daJobMovementSkills'],
    key: 'daJobMovementSkills',
    flatResults: true,
    lazyQuery: useLazyQuery(DA_JOB_MOVEMENT_SKILLS_QUERY as typeof DAJobMovementSkillsDocument)
  });
  const pollingMovementSkills =
    useChartPolling(chartId === DA_JOB_MOVEMENT_SKILLS, failedMovementSkills, movementSkills?.status);

  // lazy load Close Match Jobs data
  const {
    query: getCloseMatchJobs, pending: pendingCloseMatchJobs, failed: failedCloseMatchJobs, results: closeMatchJobs,
    error: errorCloseMatchJobs
  } = useQueryObject({
    data: undefined as unknown as DACloseMatchJobsQuery['daCloseMatchJobs'],
    key: 'daCloseMatchJobs',
    flatResults: true,
    lazyQuery: useLazyQuery(DA_CLOSE_MATCH_JOBS_QUERY as typeof DACloseMatchJobsDocument)
  });
  const pollingCloseMatchJobs =
    useChartPolling(chartId === DA_CLOSE_MATCH_JOBS, failedCloseMatchJobs, closeMatchJobs?.status);

  // lazy load Close Match Jobs data
  const {
    query: getAvgSkills, pending: pendingAvgSkills, failed: failedAvgSkills, results: avgSkills, error: errorAvgSkills
  } = useQueryObject({
    data: undefined as unknown as DAAverageSkillsQuery['daAverageSkills'],
    key: 'daAverageSkills',
    flatResults: true,
    lazyQuery: useLazyQuery(DA_AVERAGE_SKILLS_QUERY as typeof DAAverageSkillsDocument)
  });
  const pollingAvgSkills =
    useChartPolling(chartId === DA_AVERAGE_SKILLS, failedAvgSkills, avgSkills?.status);

  // lazy load Employee Velocity data
  const {
    query: getEmplVelocity, pending: pendingEmplVelocity, failed: failedEmplVelocity, results: emplVelocity,
    error: errorEmplVelocity
  } = useQueryObject({
    data: undefined as unknown as DAEmployeeVelocityQuery['daEmployeeVelocity'],
    key: 'daEmployeeVelocity',
    flatResults: true,
    lazyQuery: useLazyQuery(DA_EMPLOYEE_VELOCITY_QUERY as typeof DAEmployeeVelocityDocument)
  });
  const pollingEmplVelocity =
    useChartPolling(chartId === DA_EMPLOYEE_VELOCITY, failedEmplVelocity, emplVelocity?.status);

  // lazy load Current Role Match data
  const {
    query: getCurMatch, pending: pendingCurMatch, failed: failedCurMatch, results: curMatch, error: errorCurMatch
  } = useQueryObject({
    data: undefined as unknown as DACurrentRoleMatchQuery['daCurrentRoleMatch'],
    key: 'daCurrentRoleMatch',
    flatResults: true,
    lazyQuery: useLazyQuery(DA_CURRENT_ROLE_MATCH_QUERY as typeof DACurrentRoleMatchDocument)
  });
  const pollingCurMatch = useChartPolling(chartId === DA_CURRENT_ROLE_MATCH, failedCurMatch, curMatch?.status);

  // lazy load Current Role Match data
  const {
    query: getOrgUsage, pending: pendingOrgUsage, failed: failedOrgUsage, results: orgUsage, error: errorOrgUsage
  } = useQueryObject({
    data: undefined as unknown as DAOrgUsageQuery['daOrgUsage'],
    key: 'daOrgUsage',
    flatResults: true,
    lazyQuery: useLazyQuery(DA_USAGE_BY_ORG_QUERY as typeof DAOrgUsageDocument)
  });
  const pollingOrgUsage = useChartPolling(chartId === DA_USAGE_BY_ORG, failedOrgUsage, orgUsage?.status);

  // Every chart here carries the same `manager_id`, so any of them can be the one refused for it,
  // and the browser owns the selector that produced it.
  const scopeRefused = isScopeRefused(
    errorMovementSkills, errorCloseMatchJobs, errorAvgSkills, errorEmplVelocity, errorCurMatch, errorOrgUsage
  );
  useEffect(() => {
    if (scopeRefused) onScopeRefused?.();
  }, [scopeRefused, onScopeRefused]);

  const [matchRate, setMatchRate] = useState<number>(CONST_MATCH_RATE[3]);
  const [inDemandOnly, setInDemandOnly] = useState(false);
  const [jobMovementScope, setJobMovementScope] = useState(JOB_MOVEMENT_SKILL_SCOPES[0]);

  const handleMatchRateChange = useCallback((event: SelectChangeEvent<number>) =>
    setMatchRate(toValidConst(event?.target?.value, CONST_MATCH_RATE[3], CONST_MATCH_RATE)), []);

  // eslint-disable-next-line complexity
  useEffect(() => {
    onEmployeeCountChange?.({
      [DA_JOB_MOVEMENT_SKILLS]: !failedMovementSkills && isNil(movementSkills?.total_employees)
        ? null : movementSkills?.total_employees,
      [DA_CLOSE_MATCH_JOBS]: !failedCloseMatchJobs && isNil(closeMatchJobs?.total_employees)
        ? null : closeMatchJobs?.total_employees,
      [DA_AVERAGE_SKILLS]: !failedAvgSkills && isNil(avgSkills?.total_employees) ? null : avgSkills?.total_employees,
      [DA_EMPLOYEE_VELOCITY]: !failedEmplVelocity && isNil(emplVelocity?.total_employees)
        ? null : emplVelocity?.total_employees,
      [DA_CURRENT_ROLE_MATCH]: !failedCurMatch && isNil(curMatch?.total_employees) ? null : curMatch?.total_employees,
      [DA_USAGE_BY_ORG]: !failedOrgUsage && isNil(orgUsage?.total_employees) ? null : orgUsage?.total_employees
    }[chartId]);
  }, [
    chartId,
    movementSkills?.total_employees, failedMovementSkills,
    closeMatchJobs?.total_employees, failedCloseMatchJobs,
    avgSkills?.total_employees, failedAvgSkills,
    emplVelocity?.total_employees, failedEmplVelocity,
    curMatch?.total_employees, failedCurMatch,
    orgUsage?.total_employees, failedOrgUsage,
    onEmployeeCountChange
  ]);

  const optionalParams = useMemo(() => ({ years }), [years]);
  const optionalParams2 = useMemo(() => ({
    upwardJobMovement: jobMovementScope === JOB_MOVEMENT_SCOPE_UP
  }), [jobMovementScope]);

  const [
    exportParams,
    movementSkillsExportParams,
    closeMatchJobsExportParams,
    avgSkillsExportParams
  ] = useMemo(() => {
    if (!filters || !token || !isString(token)) return [null, null, null, null];
    const params: FilterValues & { token: string; } = { ...filters, token };
    return [
      // Employee Velocity + Current Role Match
      params,
      // Job Movement Skills
      omitParamsBySkillType(params, skillType),
      // Close Match Jobs
      { ...params, match_rate: matchRate },
      // Average # of Skills
      { ...params, duration: MAX_DURATION, ...inDemandOnly ? { indemand_only: true } : {} }
    ];
  }, [matchRate, inDemandOnly, skillType, filters, token]);

  // load Job Movement Skills
  useEffect(() => {
    if (
      chartId === DA_JOB_MOVEMENT_SKILLS && HAS_INDEMAND_SKILLS && DA_TABS?.in_demand_skills && filters &&
      pollingMovementSkills >= 0
    ) getMovementSkills?.({
      variables: {
        input: omitParamsBySkillType(filters, skillType),
        pathBuilder
      },
      ...pollingMovementSkills >= 1 ? POLLING_FETCH_POLICY : {}
    });
  }, [chartId, skillType, filters, pollingMovementSkills, getMovementSkills, HAS_INDEMAND_SKILLS, DA_TABS?.in_demand_skills]);

  // load Close Match Jobs
  useEffect(() => {
    if (
      chartId === DA_CLOSE_MATCH_JOBS && DA_TABS?.close_match_jobs && filters && matchRate && pollingCloseMatchJobs >= 0
    ) getCloseMatchJobs?.({
      variables: {
        input: {
          ...filters,
          match_rate: matchRate
        },
        pathBuilder
      },
      ...pollingCloseMatchJobs >= 1 ? POLLING_FETCH_POLICY : {}
    });
  }, [chartId, filters, matchRate, pollingCloseMatchJobs, getCloseMatchJobs, DA_TABS?.close_match_jobs]);

  // load Average # of Skills
  useEffect(() => {
    if (chartId === DA_AVERAGE_SKILLS && DA_TABS?.average_skills && filters && pollingAvgSkills >= 0) getAvgSkills?.({
      variables: {
        input: {
          ...filters,
          duration: MAX_DURATION,
          ...inDemandOnly ? { indemand_only: true } : {}
        },
        pathBuilder
      },
      ...pollingAvgSkills >= 1 ? POLLING_FETCH_POLICY : {}
    });
  }, [chartId, filters, inDemandOnly, pollingAvgSkills, getAvgSkills, DA_TABS?.average_skills]);

  // load Employee Velocity
  useEffect(() => {
    if (
      chartId === DA_EMPLOYEE_VELOCITY && DA_TABS?.employee_velocity && filters && pollingEmplVelocity >= 0
    ) getEmplVelocity?.({
      variables: {
        input: filters,
        pathBuilder
      },
      ...pollingEmplVelocity >= 1 ? POLLING_FETCH_POLICY : {}
    });
  }, [chartId, filters, pollingEmplVelocity, getEmplVelocity, DA_TABS?.employee_velocity]);

  // load Current Role Match
  useEffect(() => {
    if (chartId === DA_CURRENT_ROLE_MATCH && DA_TABS?.match_to_current_role && filters && pollingCurMatch >= 0) getCurMatch?.({
      variables: {
        input: filters,
        pathBuilder
      },
      ...pollingCurMatch >= 1 ? POLLING_FETCH_POLICY : {}
    });
  }, [chartId, filters, pollingCurMatch, getCurMatch, DA_TABS?.match_to_current_role]);

  // load Current Role Match
  useEffect(() => {
    if (chartId === DA_USAGE_BY_ORG && DA_TABS?.usage_by_org && filters && pollingOrgUsage >= 0) getOrgUsage?.({
      variables: {
        input: filters,
        pathBuilder
      },
      ...pollingOrgUsage >= 1 ? POLLING_FETCH_POLICY : {}
    });
  }, [chartId, filters, pollingOrgUsage, getOrgUsage, DA_TABS?.usage_by_org]);

  const handlePinJobMovementSkills = useCallback(() => onPin(DA_JOB_MOVEMENT_SKILLS), [onPin]);
  const handlePinCloseMatchJobs = useCallback(() => onPin(DA_CLOSE_MATCH_JOBS), [onPin]);
  const handlePinAvgSkills = useCallback(() => onPin(DA_AVERAGE_SKILLS), [onPin]);
  const handlePinEmplVelocity = useCallback(() => onPin(DA_EMPLOYEE_VELOCITY), [onPin]);
  const handlePinCurMatch = useCallback(() => onPin(DA_CURRENT_ROLE_MATCH), [onPin]);
  const handlePinOrgUsage = useCallback(() => onPin(DA_USAGE_BY_ORG), [onPin]);

  const exportDisabled = disabled || pending || !filters;

  return (
    <>
      {chartId === DA_JOB_MOVEMENT_SKILLS && HAS_INDEMAND_SKILLS && DA_TABS?.in_demand_skills ? (
        <TopChart
            variant="job_movement_skills"
            hrbp
            layout="full"
            info="hr.dashboard.job_movement_skills.info"
            withInfoButton
            data={movementSkills?.results ? take(movementSkills?.results, MAX_MANAGER_DASHBOARD_ITEMS) : null}
            totalEmployees={movementSkills?.total_employees}
            path={PATH_SKILL}
            generating={pollingMovementSkills >= 1}
            pending={pendingMovementSkills || pending}
            failed={failedMovementSkills}
            failureMessage={getScopeFetchMessage(errorMovementSkills)}
            optionalParams={optionalParams2}
            action={(
              <ScopeSelector
                  simple
                  scope={JOB_MOVEMENT_SKILL_SCOPES}
                  value={jobMovementScope}
                  onChange={setJobMovementScope as (item: string) => void}
                  disabled={disabled || pollingMovementSkills >= 1 || pendingMovementSkills ? true : undefined}
              />
            )}
            pinned={includes(pinned, DA_JOB_MOVEMENT_SKILLS)}
            onPin={disabled ? undefined : handlePinJobMovementSkills}
            pinning={pinning}
            exportEndpoint={API_DA_JOB_MOVEMENT_SKILLS_EXPORT}
            exportParams={movementSkillsExportParams}
            exportDisabled={exportDisabled || pollingMovementSkills >= 1 || pendingMovementSkills || failedMovementSkills ||
              size(movementSkills?.results) < 1}
        />
      ) : undefined}
      {chartId === DA_CLOSE_MATCH_JOBS && DA_TABS?.close_match_jobs ? (
        <TopChart
            variant="close_match_jobs"
            hrbp
            layout="full"
            info="hr.dashboard.close_match_jobs.info"
            withInfoButton
            data={closeMatchJobs?.results}
            totalEmployees={closeMatchJobs?.total_employees}
            generating={pollingCloseMatchJobs >= 1}
            pending={pendingCloseMatchJobs || pending}
            failed={failedCloseMatchJobs}
            failureMessage={getScopeFetchMessage(errorCloseMatchJobs)}
            optionalParams={optionalParams}
            action={(
              <ConstSelector
                  variant="match_rate"
                  value={matchRate}
                  onChange={handleMatchRateChange}
                  disabled={disabled || pollingCloseMatchJobs >= 1 || pendingCloseMatchJobs ? true : undefined}
                  className={matchRateSelector}
              />
            )}
            pinned={includes(pinned, DA_CLOSE_MATCH_JOBS)}
            onPin={disabled ? undefined : handlePinCloseMatchJobs}
            pinning={pinning}
            exportEndpoint={API_DA_CLOSE_MATCH_JOBS_EXPORT}
            exportParams={closeMatchJobsExportParams}
            exportDisabled={exportDisabled || pollingCloseMatchJobs >= 1 || pendingCloseMatchJobs || failedCloseMatchJobs ||
              size(closeMatchJobs?.results) < 1}
        />
      ) : undefined}
      {chartId === DA_AVERAGE_SKILLS && DA_TABS?.average_skills ? (
        <TopChart
            variant="job_average_skills"
            hrbp
            info="hr.dashboard.job_average_skills.info"
            withInfoButton
            layout="full"
            data={avgSkills?.results}
            totalEmployees={avgSkills?.total_employees}
            generating={pollingAvgSkills >= 1}
            pending={pendingAvgSkills || pending}
            failed={failedAvgSkills}
            failureMessage={getScopeFetchMessage(errorAvgSkills)}
            optionalParams={optionalParams}
            action={(
              <OnOffSwitch
                  label="hr.dashboard.in_demand_only"
                  value={inDemandOnly}
                  onChange={setInDemandOnly}
                  disabled={disabled || pollingAvgSkills >= 1 || pendingAvgSkills ? true : undefined}
              />
            )}
            pinned={includes(pinned, DA_AVERAGE_SKILLS)}
            onPin={disabled ? undefined : handlePinAvgSkills}
            pinning={pinning}
            exportEndpoint={API_DA_AVERAGE_SKILLS_EXPORT}
            exportParams={avgSkillsExportParams}
            exportDisabled={exportDisabled || pollingAvgSkills >= 1 || pendingAvgSkills || failedAvgSkills ||
              size(avgSkills?.results) < 1}
        />
      ) : undefined}
      {chartId === DA_EMPLOYEE_VELOCITY && DA_TABS?.employee_velocity ? (
        <TopChart
            variant="employee_velocity"
            hrbp
            layout="full"
            info="hr.dashboard.employee_velocity.info"
            withInfoButton
            data={emplVelocity?.results}
            totalEmployees={emplVelocity?.total_employees}
            generating={pollingEmplVelocity >= 1}
            pending={pendingEmplVelocity || pending}
            failed={failedEmplVelocity}
            failureMessage={getScopeFetchMessage(errorEmplVelocity)}
            optionalParams={optionalParams}
            pinned={includes(pinned, DA_EMPLOYEE_VELOCITY)}
            onPin={disabled ? undefined : handlePinEmplVelocity}
            pinning={pinning}
            exportEndpoint={API_DA_EMPLOYEE_VELOCITY_EXPORT}
            exportParams={exportParams}
            exportDisabled={exportDisabled || pollingEmplVelocity >= 1 || pendingEmplVelocity || failedEmplVelocity ||
              size(emplVelocity?.results) < 1}
        />
      ) : undefined}
      {chartId === DA_CURRENT_ROLE_MATCH && DA_TABS?.match_to_current_role ? (
        <TopChart
            variant="current_role_match"
            hrbp
            layout="full"
            info="hr.dashboard.current_role_match.info"
            withInfoButton
            data={curMatch?.results}
            totalEmployees={curMatch?.total_employees}
            generating={pollingCurMatch >= 1}
            pending={pendingCurMatch || pending}
            failed={failedCurMatch}
            failureMessage={getScopeFetchMessage(errorCurMatch)}
            optionalParams={optionalParams}
            pinned={includes(pinned, DA_CURRENT_ROLE_MATCH)}
            onPin={disabled ? undefined : handlePinCurMatch}
            pinning={pinning}
            exportEndpoint={API_DA_CURRENT_ROLE_MATCH_EXPORT}
            exportParams={exportParams}
            exportDisabled={exportDisabled || pollingCurMatch >= 1 || pendingCurMatch || failedCurMatch ||
              size(curMatch?.results) < 1}
        />
      ) : undefined}
      {chartId === DA_USAGE_BY_ORG && DA_TABS?.usage_by_org ? (
        <TopChart
            variant="usage_by_org"
            hrbp
            layout="full"
            // info="hr.dashboard.usage_by_org.info"
            withInfoButton
            data={orgUsage?.results}
            totalEmployees={orgUsage?.total_employees}
            generating={pollingOrgUsage >= 1}
            pending={pendingOrgUsage || pending}
            failed={failedOrgUsage}
            failureMessage={getScopeFetchMessage(errorOrgUsage)}
            optionalParams={optionalParams}
            pinned={includes(pinned, DA_USAGE_BY_ORG)}
            onPin={disabled ? undefined : handlePinOrgUsage}
            pinning={pinning}
            exportEndpoint={API_DA_USAGE_BY_ORG_EXPORT}
            exportParams={exportParams}
            exportDisabled={exportDisabled || pollingOrgUsage >= 1 || pendingOrgUsage || failedOrgUsage ||
              size(orgUsage?.results) < 1}
        />
      ) : undefined}
    </>
  );
};

export default memo(DashboardAnalyticsEmployees);
